/**
 * Watch Website Modal
 * 
 * Unified experience for website monitoring:
 * 1. Explains what watching does
 * 2. URL input with smart detection
 * 3. AI interpreter to understand what signals matter
 * 4. Field selection for output customization
 * 5. Immediate first run on activation
 * 
 * Philosophy: Simple setup, immediate results, smart monitoring
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  Globe, 
  Loader2, 
  Link2,
  Bell,
  Sparkles,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Briefcase,
  Newspaper,
  Users,
  Building2,
  TrendingUp,
  Mail,
  Phone,
  Linkedin,
  MapPin,
  Play,
  Eye,
  Zap
} from 'lucide-react';
import api from '../../utils/axios';

// URL Type Detection
const URL_TYPES = {
  careers: { 
    icon: Briefcase, 
    label: 'Careers Page', 
    color: 'bg-emerald-500',
    description: 'Monitor job postings and hiring activity',
    defaultFields: ['jobTitle', 'department', 'location', 'postedDate', 'applyLink']
  },
  linkedin: { 
    icon: Users, 
    label: 'LinkedIn', 
    color: 'bg-blue-600',
    description: 'Track company updates and team changes',
    defaultFields: ['name', 'title', 'company', 'location']
  },
  news: { 
    icon: Newspaper, 
    label: 'News Site', 
    color: 'bg-orange-500',
    description: 'Watch for company mentions and announcements',
    defaultFields: ['headline', 'source', 'date', 'snippet']
  },
  company: { 
    icon: Building2, 
    label: 'Company Site', 
    color: 'bg-purple-500',
    description: 'Monitor for updates, news, and hiring signals',
    defaultFields: ['companyName', 'industry', 'location', 'website']
  }
};

// Available fields for website monitoring
const WEBSITE_FIELDS = {
  job: {
    label: 'Job Postings',
    description: 'Open roles found on the site',
    fields: [
      { id: 'jobTitle', label: 'Job Title', default: true },
      { id: 'department', label: 'Department', default: true },
      { id: 'location', label: 'Location', default: true },
      { id: 'salary', label: 'Salary', default: false },
      { id: 'postedDate', label: 'Posted Date', default: true },
      { id: 'applyLink', label: 'Apply Link', default: true },
      { id: 'employmentType', label: 'Employment Type', default: false },
      { id: 'applicants', label: 'Applicant Count', default: false },
    ]
  },
  contact: {
    label: 'People & Contacts',
    description: 'Decision makers and hiring managers',
    fields: [
      { id: 'name', label: 'Full Name', default: true },
      { id: 'title', label: 'Job Title', default: true },
      { id: 'email', label: 'Email', default: true },
      { id: 'phone', label: 'Phone', default: false },
      { id: 'linkedin', label: 'LinkedIn', default: true },
      { id: 'department', label: 'Department', default: false },
    ]
  },
  company: {
    label: 'Company Intel',
    description: 'Organization information',
    fields: [
      { id: 'companyName', label: 'Company Name', default: true },
      { id: 'industry', label: 'Industry', default: true },
      { id: 'location', label: 'Location', default: true },
      { id: 'website', label: 'Website', default: true },
      { id: 'employeeCount', label: 'Employee Count', default: false },
      { id: 'revenue', label: 'Revenue', default: false },
    ]
  },
  news: {
    label: 'News & Signals',
    description: 'Updates and announcements',
    fields: [
      { id: 'headline', label: 'Headline', default: true },
      { id: 'source', label: 'Source', default: true },
      { id: 'date', label: 'Date', default: true },
      { id: 'snippet', label: 'Snippet', default: true },
      { id: 'url', label: 'Article Link', default: true },
      { id: 'thumbnail', label: 'Thumbnail', default: false },
    ]
  }
};

// Detect URL type from the URL
function detectUrlType(url) {
  if (!url) return 'company';
  const lowerUrl = url.toLowerCase();
  
  if (lowerUrl.includes('/careers') || lowerUrl.includes('/jobs') || 
      lowerUrl.includes('greenhouse.io') || lowerUrl.includes('lever.co') || 
      lowerUrl.includes('workday.com') || lowerUrl.includes('icims.com') ||
      lowerUrl.includes('ashbyhq.com') || lowerUrl.includes('bamboohr.com')) {
    return 'careers';
  }
  if (lowerUrl.includes('linkedin.com')) {
    return 'linkedin';
  }
  if (lowerUrl.includes('news') || lowerUrl.includes('reuters') || 
      lowerUrl.includes('bloomberg') || lowerUrl.includes('techcrunch') ||
      lowerUrl.includes('wsj.com') || lowerUrl.includes('forbes.com')) {
    return 'news';
  }
  return 'company';
}

// Extract domain from URL for display
function extractDomain(url) {
  if (!url) return '';
  try {
    // Normalize URL first
    let normalized = url.trim();
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
      normalized = `https://${normalized}`;
    }
    // If URL doesn't have a dot (e.g., just "stripe"), it's incomplete
    // Try adding .com as a common fallback
    if (!normalized.includes('.')) {
      normalized = normalized + '.com';
    }
    const parsed = new URL(normalized);
    return parsed.hostname.replace('www.', '');
  } catch {
    // If still can't parse, check if it looks like a domain without extension
    const cleanUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
    if (cleanUrl && !cleanUrl.includes('.')) {
      return cleanUrl + '.com';
    }
    return url;
  }
}

// Get default fields based on URL type
function getDefaultFields(urlType) {
  const defaults = {};
  Object.entries(WEBSITE_FIELDS).forEach(([category, config]) => {
    config.fields.forEach(field => {
      defaults[field.id] = field.default;
    });
  });
  return defaults;
}

export default function WatchWebsiteModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  initialUrl = ''
}) {
  // URL State
  const [url, setUrl] = useState(initialUrl);
  const [urlType, setUrlType] = useState('company');
  const [domain, setDomain] = useState('');
  
  // AI Interpretation State
  const [intent, setIntent] = useState('');
  const [isInterpreting, setIsInterpreting] = useState(false);
  const [interpretation, setInterpretation] = useState(null);
  
  // Field Selection State
  const [showFields, setShowFields] = useState(false);
  const [enabledFields, setEnabledFields] = useState(() => getDefaultFields('company'));
  
  // Config State
  const [frequency, setFrequency] = useState('daily');
  const [generateDrafts, setGenerateDrafts] = useState(true);
  
  // UI State
  const [step, setStep] = useState(1); // 1: URL, 2: Configure, 3: Activating
  const [isActivating, setIsActivating] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus input on open
  useEffect(() => {
    if (isOpen && inputRef.current && step === 1) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, step]);

  // Update URL type when URL changes
  useEffect(() => {
    if (url) {
      const detected = detectUrlType(url);
      setUrlType(detected);
      setDomain(extractDomain(url));
      
      // Update default fields based on URL type
      const typeConfig = URL_TYPES[detected];
      if (typeConfig?.defaultFields) {
        const newDefaults = { ...enabledFields };
        typeConfig.defaultFields.forEach(fieldId => {
          newDefaults[fieldId] = true;
        });
        setEnabledFields(newDefaults);
      }
    }
  }, [url]);

  // AI Interpretation of intent
  const interpretIntent = useCallback(async (text) => {
    if (!text || text.length < 10) {
      setInterpretation(null);
      return;
    }

    setIsInterpreting(true);
    try {
      const response = await api.post('/api/ai/interpret-scout', {
        intent: text,
        context: { urlType, domain, url }
      });

      if (response.data?.success) {
        setInterpretation(response.data.interpretation);
      }
    } catch (err) {
      console.error('Interpretation error:', err);
      // Fallback - don't show error, just continue
    } finally {
      setIsInterpreting(false);
    }
  }, [urlType, domain, url]);

  // Debounced interpretation
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (intent.length >= 10) {
      debounceRef.current = setTimeout(() => {
        interpretIntent(intent);
      }, 600);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [intent, interpretIntent]);

  // Toggle field
  const toggleField = (fieldId) => {
    setEnabledFields(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  };

  // Proceed to step 2
  const handleUrlSubmit = () => {
    if (!url.trim()) return;
    
    // Normalize URL - add protocol and ensure valid domain
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = 'https://' + normalizedUrl;
    }
    // If URL doesn't have a dot (e.g., "stripe" without ".com"), add .com
    const urlWithoutProtocol = normalizedUrl.replace(/^https?:\/\//, '');
    if (!urlWithoutProtocol.includes('.')) {
      normalizedUrl = normalizedUrl + '.com';
    }
    setUrl(normalizedUrl);
    setStep(2);
  };

  // Activate watching
  const handleActivate = async () => {
    if (!url.trim()) return;

    setIsActivating(true);
    setError(null);
    setStep(3);

    try {
      // Normalize URL - add protocol and ensure valid domain
      let normalizedUrl = url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }
      // If URL doesn't have a dot (e.g., "stripe" without ".com"), add .com
      const urlWithoutProtocol = normalizedUrl.replace(/^https?:\/\//, '');
      if (!urlWithoutProtocol.includes('.')) {
        normalizedUrl = normalizedUrl + '.com';
      }

      // Build scout config
      const scoutConfig = {
        strategy: 'scrape',
        websites: [normalizedUrl],
        urlType,
        intent: intent || `Monitor ${domain} for updates`,
        keywords: interpretation?.keywords || [domain],
        sources: interpretation?.sources || ['jobs', 'news'],
        generateDrafts,
        outputFields: Object.entries(enabledFields)
          .filter(([_, enabled]) => enabled)
          .map(([id]) => id)
      };

      // Step 1: Create saved search
      // Note: leadType should be 'contact' or 'client' - 'job' is not a valid lead_type
      const requestBody = {
        customName: `Watch: ${domain}`,
        naturalQuery: intent || `Monitor ${domain} for job postings, news, and updates`,
        leadType: 'contact', // Website watching produces contact/signal leads
        searchCriteria: {
          websites: [normalizedUrl],
          urlType,
          domain,
          isWebsiteWatch: true // Flag to indicate this is website monitoring
        }
      };
      
      
      const createResponse = await api.post('/api/saved-searches', requestBody);
      

      if (!createResponse.data?.success || !createResponse.data?.savedSearch?.id) {
        throw new Error('Failed to create saved search');
      }

      const savedSearchId = createResponse.data.savedSearch.id;

      // Step 2: Enable alert with scout config
      await api.post(`/api/leads/alerts/${savedSearchId}/toggle`, {
        isAlert: true,
        frequency,
        scoutConfig
      });

      // Step 3: Run IMMEDIATELY - don't wait for scheduler
      setIsRunning(true);
      
      const runResponse = await api.post(`/api/scout/run/${savedSearchId}`);
      
      const leadsFound = runResponse.data?.result?.leadsCreated || 
                         runResponse.data?.result?.signalsCount || 0;

      setResult({
        savedSearchId,
        domain,
        leadsFound,
        message: leadsFound > 0 
          ? `Found ${leadsFound} leads from ${domain}` 
          : `Now watching ${domain} - leads will appear as they're found`
      });

      // Notify parent
      onSuccess?.({
        id: savedSearchId,
        name: `Watch: ${domain}`,
        leadsFound,
        url: normalizedUrl
      });

    } catch (err) {
      console.error('Activation error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to start watching');
      setStep(2); // Go back to configure step
    } finally {
      setIsActivating(false);
      setIsRunning(false);
    }
  };

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setUrl(initialUrl);
      setUrlType('company');
      setDomain('');
      setIntent('');
      setInterpretation(null);
      setStep(1);
      setError(null);
      setResult(null);
      setEnabledFields(getDefaultFields('company'));
    }
  }, [isOpen, initialUrl]);

  if (!isOpen) return null;

  const TypeIcon = URL_TYPES[urlType]?.icon || Globe;
  const typeColor = URL_TYPES[urlType]?.color || 'bg-gray-500';
  const typeLabel = URL_TYPES[urlType]?.label || 'Website';
  const typeDescription = URL_TYPES[urlType]?.description || 'Monitor for updates';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-[#f8f8f6] rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-black/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center shadow-lg">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                Watch Website
              </h2>
              <p className="text-xs text-black/50">
                {step === 1 && 'Paste a URL to start monitoring'}
                {step === 2 && 'Configure what to watch for'}
                {step === 3 && (isRunning ? 'Finding leads...' : 'Setting up...')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-full transition-colors">
            <X className="w-5 h-5 text-black/40" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          
          {/* Step 1: URL Input */}
          {step === 1 && (
            <div className="space-y-5">
              {/* Explanation */}
              <div className="p-4 bg-gradient-to-r from-[#5a20ff]/5 to-[#52dcb3]/5 rounded-xl border border-[#5a20ff]/10">
                <h3 className="text-sm font-semibold text-black mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  How it works
                </h3>
                <ul className="space-y-2 text-sm text-black/70">
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#5a20ff] text-white text-xs flex items-center justify-center mt-0.5 flex-shrink-0">1</div>
                    <span>Scout visits the website and finds job postings, news, or updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#5a20ff] text-white text-xs flex items-center justify-center mt-0.5 flex-shrink-0">2</div>
                    <span>AI identifies the right decision makers to contact</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#52dcb3] text-white text-xs flex items-center justify-center mt-0.5 flex-shrink-0">3</div>
                    <span>You get leads with a reason to call and draft outreach</span>
                  </li>
                </ul>
              </div>

              {/* URL Input */}
              <div>
                <label className="block text-xs font-medium text-black/50 uppercase tracking-wide mb-2">
                  Website URL
                </label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-black/30" />
                  <input
                    ref={inputRef}
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                    placeholder="e.g., stripe.com/jobs or linkedin.com/company/openai"
                    className="w-full pl-11 pr-4 py-3 bg-white border-2 border-black/10 rounded-xl text-black placeholder-black/30 focus:outline-none focus:border-[#5a20ff] transition-colors"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  />
                </div>
              </div>

              {/* URL Type Preview */}
              {url && (
                <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-black/10">
                  <div className={`w-10 h-10 rounded-xl ${typeColor} flex items-center justify-center`}>
                    <TypeIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {domain || 'Unknown'}
                    </p>
                    <p className="text-xs text-black/50">{typeLabel} • {typeDescription}</p>
                  </div>
                  <Check className="w-5 h-5 text-[#52dcb3]" />
                </div>
              )}

              {/* Quick Examples */}
              <div>
                <p className="text-xs text-black/40 mb-2">Try these examples:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { url: 'stripe.com/jobs', label: 'Stripe Careers' },
                    { url: 'linkedin.com/company/anthropic', label: 'Anthropic LinkedIn' },
                    { url: 'apple.com/careers', label: 'Apple Careers' }
                  ].map(example => (
                    <button
                      key={example.url}
                      onClick={() => setUrl(example.url)}
                      className="px-3 py-1.5 bg-white border border-black/10 rounded-full text-xs text-black/60 hover:border-[#5a20ff]/30 hover:text-[#5a20ff] transition-colors"
                    >
                      {example.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Configure */}
          {step === 2 && (
            <div className="space-y-5">
              
              {/* URL Summary */}
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-black/10">
                <div className={`w-10 h-10 rounded-xl ${typeColor} flex items-center justify-center`}>
                  <TypeIcon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {domain}
                  </p>
                  <p className="text-xs text-black/50">{typeLabel}</p>
                </div>
                <button 
                  onClick={() => setStep(1)}
                  className="text-xs text-[#5a20ff] hover:underline"
                >
                  Change
                </button>
              </div>

              {/* Intent Input (Optional) */}
              <div>
                <label className="block text-xs font-medium text-black/50 uppercase tracking-wide mb-2">
                  What signals matter to you? <span className="text-black/30">(optional)</span>
                </label>
                <div className="relative">
                  <Sparkles className="absolute left-3 top-3 w-4 h-4 text-[#5a20ff]/50" />
                  <textarea
                    value={intent}
                    onChange={(e) => setIntent(e.target.value)}
                    placeholder="e.g., 'Engineering roles in NYC' or 'VP-level hires' or 'Series A funding news'"
                    className="w-full pl-10 pr-4 py-3 bg-white border border-black/10 rounded-xl text-sm text-black placeholder-black/30 focus:outline-none focus:border-[#5a20ff] transition-colors resize-none h-20"
                  />
                  {isInterpreting && (
                    <div className="absolute right-3 bottom-3 flex items-center gap-1.5 text-[#5a20ff]">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span className="text-xs">Thinking...</span>
                    </div>
                  )}
                </div>
                
                {/* AI Interpretation Preview */}
                {interpretation && !isInterpreting && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {interpretation.keywords?.slice(0, 5).map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 bg-[#5a20ff]/5 rounded-full text-xs text-[#5a20ff]">
                        {kw}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Field Selection */}
              <div>
                <button
                  onClick={() => setShowFields(!showFields)}
                  className="w-full flex items-center justify-between p-3 bg-white rounded-xl border border-black/10 hover:border-[#5a20ff]/30 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#5a20ff]" />
                    <span className="text-sm text-black/70">
                      Choose which fields to show
                    </span>
                    <span className="text-xs text-black/40">
                      ({Object.values(enabledFields).filter(Boolean).length} selected)
                    </span>
                  </div>
                  {showFields ? (
                    <ChevronDown className="w-4 h-4 text-black/40" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-black/40" />
                  )}
                </button>

                {showFields && (
                  <div className="mt-2 p-4 bg-white rounded-xl border border-black/10 space-y-4 max-h-60 overflow-y-auto">
                    {Object.entries(WEBSITE_FIELDS).map(([categoryId, category]) => (
                      <div key={categoryId}>
                        <p className="text-xs font-medium text-black/50 uppercase tracking-wide mb-2">
                          {category.label}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {category.fields.map(field => (
                            <button
                              key={field.id}
                              onClick={() => toggleField(field.id)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${
                                enabledFields[field.id]
                                  ? 'bg-[#5a20ff]/10 text-[#5a20ff] border border-[#5a20ff]/20'
                                  : 'bg-black/5 text-black/50 border border-transparent hover:bg-black/10'
                              }`}
                            >
                              <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center ${
                                enabledFields[field.id]
                                  ? 'border-[#5a20ff] bg-[#5a20ff]'
                                  : 'border-black/20'
                              }`}>
                                {enabledFields[field.id] && (
                                  <Check className="w-2 h-2 text-white" />
                                )}
                              </div>
                              {field.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Frequency */}
              <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-black/10">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-black/40" />
                  <span className="text-sm text-black/70">Check frequency</span>
                </div>
                <div className="flex gap-1">
                  {['hourly', 'daily', 'weekly'].map((f) => (
                    <button
                      key={f}
                      onClick={() => setFrequency(f)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        frequency === f
                          ? 'bg-[#5a20ff] text-white'
                          : 'bg-black/5 text-black/50 hover:bg-black/10'
                      }`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Draft Generation Toggle */}
              <button
                onClick={() => setGenerateDrafts(!generateDrafts)}
                className="w-full flex items-center justify-between p-3 bg-white rounded-xl border border-black/10"
              >
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-black/40" />
                  <span className="text-sm text-black/70">Auto-generate outreach drafts</span>
                </div>
                <div className={`w-10 h-6 rounded-full transition-colors ${
                  generateDrafts ? 'bg-[#5a20ff]' : 'bg-black/10'
                } flex items-center p-0.5`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    generateDrafts ? 'translate-x-4' : 'translate-x-0'
                  }`} />
                </div>
              </button>

              {/* Error */}
              {error && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Activating */}
          {step === 3 && (
            <div className="py-8 space-y-6">
              {!result ? (
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                  <h3 className="text-lg font-semibold text-black mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {isRunning ? 'Finding leads...' : 'Setting up Scout...'}
                  </h3>
                  <p className="text-sm text-black/50">
                    {isRunning 
                      ? `Scanning ${domain} for opportunities` 
                      : 'Creating your monitoring feed'}
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#52dcb3] flex items-center justify-center">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-black mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {result.leadsFound > 0 
                      ? `Found ${result.leadsFound} leads!` 
                      : 'Watching started'}
                  </h3>
                  <p className="text-sm text-black/50 mb-4">
                    {result.message}
                  </p>
                  
                  {result.leadsFound > 0 && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#52dcb3]/10 rounded-full text-[#52dcb3]">
                      <Zap className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {result.leadsFound} leads ready in your feed
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-black/5 bg-white/50 flex items-center justify-between flex-shrink-0">
          {step === 1 && (
            <>
              <button 
                onClick={onClose} 
                className="text-sm text-black/50 hover:text-black transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUrlSubmit}
                disabled={!url.trim()}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${
                  url.trim()
                    ? 'bg-[#5a20ff] text-white hover:bg-[#5a20ff]/90 shadow-lg'
                    : 'bg-black/10 text-black/40 cursor-not-allowed'
                }`}
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
          
          {step === 2 && (
            <>
              <button 
                onClick={() => setStep(1)} 
                className="text-sm text-black/50 hover:text-black transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleActivate}
                disabled={isActivating}
                className="px-6 py-2.5 rounded-full text-sm font-semibold flex items-center gap-2 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white hover:opacity-90 shadow-lg transition-all"
              >
                {isActivating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Starting...</span>
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    <span>Start Watching</span>
                  </>
                )}
              </button>
            </>
          )}
          
          {step === 3 && result && (
            <button
              onClick={onClose}
              className="w-full px-6 py-2.5 rounded-full text-sm font-semibold bg-[#5a20ff] text-white hover:bg-[#5a20ff]/90 shadow-lg transition-all"
            >
              {result.leadsFound > 0 ? 'View Leads' : 'Done'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

